import React, { useState, useEffect } from 'react'
import { Box } from 'components'
import { TextField, Button } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { fetchCanadianCities, searchCanadianCities } from 'utils/bipoc'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  input: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  applyButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    }
  }
}))

// Simple direct implementation that updates the global filter state immediately
export function CityFilter({ filterState, setFilterState, closeMenu }) {
  const classes = useStyles()
  const [cities, setCities] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')

  // Fetch cities on component mount
  useEffect(() => {
    const loadCities = async () => {
      setLoading(true)
      try {
        const canadianCities = await fetchCanadianCities()
        setCities(canadianCities)
      } catch (error) {
        console.error('Error loading Canadian cities:', error)
      } finally {
        setLoading(false)
      }
    }

    loadCities()
  }, [])

  // Search cities when user types
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (searchInput.trim().length > 1) {
        setLoading(true)
        try {
          const searchResults = await searchCanadianCities(searchInput)
          setCities(searchResults)
        } catch (error) {
          console.error('Error searching cities:', error)
        } finally {
          setLoading(false)
        }
      }
    }, 300) // Shorter debounce

    return () => clearTimeout(searchTimer)
  }, [searchInput])

  // Handler that updates the filter state WITHOUT closing the dialog
  const handleCityChange = (event, newValue, reason) => {
    // Stop event propagation to prevent dialog from closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // console.log('City selected:', newValue, 'reason:', reason);

    if (newValue) {
      // Format the city data
      const cityFilter = {
        id: newValue.id,
        label: newValue.name || newValue.label,
        province: newValue.province || ''
      }

      // console.log('Updating global filter state with city:', cityFilter);

      // Update the global filter state directly without closing the dialog
      setFilterState(current => ({
        ...current,
        cityFilter: cityFilter
      }));
    } else if (reason === 'clear') {
      // Clear the city filter
      // console.log('Clearing city filter');
      setFilterState(current => {
        const newState = { ...current };
        delete newState.cityFilter;
        return newState;
      });
    }
  }

  // Need to extract current value from filterState for the autocomplete
  const selectedCity = filterState.cityFilter || null;

  // Handle regular input changes without selecting a city
  const handleSearchInputChange = (event, value) => {
    setSearchInput(value);
  };

  // Prevent dialog from closing when clicking in autocomplete
  const preventDialogClose = (event) => {
    event.stopPropagation();
  };

  // Function to manually close the dialog
  const applyAndCloseDialog = (event) => {
    event.stopPropagation();

    // Find the parent dialog's apply button and click it
    try {
      const dialog = document.querySelector('.MuiDialog-root');
      if (dialog) {
        const applyButton = dialog.querySelector('button[type="submit"]');
        if (applyButton) {
          applyButton.click();
        }
      }
    } catch (e) {
      console.error('Error closing dialog:', e);
    }
  };

  return (
    <Box
      py={1}
      width="100%"
      minWidth={250}
      onClick={preventDialogClose} // Prevent clicks from bubbling up
      onMouseDown={preventDialogClose} // Prevent mouse down from bubbling up
    >
      <Autocomplete
        id="city-filter"
        options={cities}
        getOptionLabel={(option) => {
          if (!option) return '';
          return option.name || option.label || '';
        }}
        value={selectedCity}
        onChange={handleCityChange}
        onInputChange={handleSearchInputChange}
        className={classes.input}
        loading={loading}
        inputValue={searchInput}
        freeSolo={false}
        autoComplete
        autoHighlight
        disableClearable={false}
        // Prevent clicks in the dropdown from closing the dialog
        onMouseDown={preventDialogClose}
        onClick={preventDialogClose}
        renderOption={(option, { selected }) => (
          <div
            onClick={preventDialogClose}
            onMouseDown={preventDialogClose}
            style={{
              fontWeight: selected ? 700 : 400,
              padding: '6px 0'
            }}
          >
            {option.name || option.label}
            {option.province && (
              <span style={{ marginLeft: 8, fontSize: '0.875rem', color: '#666' }}>
                {option.province}
              </span>
            )}
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="City Search"
            variant="outlined"
            fullWidth
            placeholder="Start typing a city name"
            onClick={preventDialogClose}
            onMouseDown={preventDialogClose}
            onFocus={preventDialogClose}
            InputProps={{
              ...params.InputProps,
              onClick: preventDialogClose,
              onMouseDown: preventDialogClose,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <div className="MuiCircularProgress-root MuiCircularProgress-colorPrimary MuiCircularProgress-indeterminate"
                      style={{ width: 20, height: 20 }}
                      role="progressbar"
                      onClick={preventDialogClose}
                      onMouseDown={preventDialogClose}>
                      <svg className="MuiCircularProgress-svg" viewBox="22 22 44 44">
                        <circle
                          className="MuiCircularProgress-circle MuiCircularProgress-circleIndeterminate"
                          cx="44"
                          cy="44"
                          r="20.2"
                          fill="none"
                          strokeWidth="3.6"
                        />
                      </svg>
                    </div>
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      {/* Material UI Apply Button */}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          className={classes.applyButton}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // Use the closeMenu prop if provided
            if (typeof closeMenu === 'function') {
              closeMenu();
            }
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  )
}
